import type { AutocompleteRenderInputParams, SxProps } from '@mui/material'
import {
  Autocomplete as MUIAutocomplete,
  TextField as MUITextField,
  CircularProgress as MUICircularProgress,
} from '@mui/material'
import { Box } from '@/CommonComponents'
export interface IOption {
  id: string
  label: string
}
interface IAutocompleteProps {
  options: IOption[]
  values: string[] | string
  placeholder?: string
  multiple?: boolean
  onChange?: (values: string[] | string) => void
  size?: 'small' | 'medium'
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, option: IOption) => React.ReactNode
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode
  loading?: boolean
  sx?: SxProps
  onBlur?: () => void
  autoFocus?: boolean
  cleanDisplay?: boolean
  disabled?: boolean
}

const EMPTY_OPTION: IOption = { id: '', label: '' }

function getMUIValue(values: string[] | string, options: IOption[]): IOption | IOption[] {
  if (Array.isArray(values)) {
    return values.map((value) => options.find((option) => option.id === value) || EMPTY_OPTION)
  } else {
    return options.find((option) => option.id === values) || EMPTY_OPTION
  }
}

const STYLE_WITHOUT_UNDERLINE = {
  '& .MuiInput-underline:before': {
    borderBottom: 'none',
  },
  '& .MuiInput-underline:after': {
    borderBottom: 'none',
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottom: 'none',
  },
}
function LoadingBox() {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
      }}
    >
      <MUICircularProgress size={20} />
    </Box>
  )
}
export default function Autocomplete({
  options,
  values,
  placeholder,
  onChange,
  multiple,
  renderOption,
  size = 'small',
  renderInput,
  loading,
  sx,
  onBlur,
  autoFocus,
  cleanDisplay,
  disabled,
}: IAutocompleteProps) {
  const defaultRenderIntput = (params: AutocompleteRenderInputParams) => {
    return <MUITextField {...params} label={placeholder} variant="standard" size={size} />
  }
  return (
    <Box sx={{ position: 'relative', ...sx }}>
      {loading && <LoadingBox />}
      <MUIAutocomplete
        multiple={multiple}
        renderInput={renderInput || defaultRenderIntput}
        options={options}
        disabled={disabled}
        popupIcon={cleanDisplay ? null : undefined}
        clearIcon={cleanDisplay ? null : undefined}
        value={getMUIValue(values, options)}
        loading={loading}
        loadingText="Loading..."
        renderOption={renderOption}
        getOptionKey={() => crypto.randomUUID()}
        getOptionLabel={(option) => {
          return option.label || ''
        }}
        sx={{
          ...(cleanDisplay ? STYLE_WITHOUT_UNDERLINE : undefined),
        }}
        autoFocus={autoFocus}
        onBlur={onBlur}
        size={size}
        onChange={(_, newValues) => {
          if (Array.isArray(newValues)) {
            onChange?.(newValues.map((value) => value.id || ''))
          } else {
            onChange?.(newValues?.id || '')
          }
        }}
      />
    </Box>
  )
}
